/**
 * ExecutionsModule — Gestión completa de planes de ejecución.
 * Vistas: Lista | Crear | Editar | Monitor | Histórico
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates,
  verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAppStore } from '../../store';
import { executions as executionsApi, systems as systemsApi, versions as versionsApi, requirements as reqApi, scenarios as scenariosApi, cases as casesApi, agentsApi, datasetApi } from '../../services/api';
import { ExecutionMonitor } from './ExecutionMonitor';
import { EvidenceReport } from './EvidenceReport';
import type { ExecutionPlan, ExecutionRun, TestSystem, Version, Requirement, Scenario, TestCase, PlanStatus, Agent } from '../../types';

// ─── Status config ─────────────────────────────────────────────────────────

const VERSION_STATUS_CFG: Record<string, { color: string; bg: string; label: string }> = {
  draft:    { color: '#854F0B', bg: '#FAEEDA', label: 'Borrador' },
  active:   { color: '#0F6E56', bg: '#E1F5EE', label: 'Activa' },
  archived: { color: '#5F5E5A', bg: '#F1EFE8', label: 'Archivada' },
};

const PLAN_STATUS: Record<PlanStatus, { color: string; bg: string; label: string; icon: string }> = {
  pending:   { color: '#5F5E5A', bg: '#F1EFE8', label: 'Pendiente',  icon: '○' },
  queued:    { color: '#185FA5', bg: '#E6F1FB', label: 'En cola',    icon: '◎' },
  running:   { color: '#185FA5', bg: '#E6F1FB', label: 'Ejecutando', icon: '◉' },
  passed:    { color: '#0F6E56', bg: '#E1F5EE', label: 'Completado', icon: '✓' },
  failed:    { color: '#A32D2D', bg: '#FCEBEB', label: 'Falló',      icon: '✗' },
  cancelled: { color: '#5F5E5A', bg: '#F1EFE8', label: 'Cancelado',  icon: '⊘' },
};

const CRITICALITY_CFG: Record<string, { color: string; bg: string; label: string }> = {
  critical: { color: '#A32D2D', bg: '#FCEBEB', label: 'Crítica' },
  high:     { color: '#854F0B', bg: '#FAEEDA', label: 'Alta' },
  medium:   { color: '#185FA5', bg: '#E6F1FB', label: 'Media' },
  low:      { color: '#5F5E5A', bg: '#F1EFE8', label: 'Baja' },
};

const EDITABLE_STATUSES: PlanStatus[] = ['pending', 'failed', 'passed', 'cancelled'];
const DELETABLE_STATUSES: PlanStatus[] = ['pending', 'failed', 'passed', 'cancelled'];
const LIVE_STATUSES: PlanStatus[] = ['running', 'queued'];

// ─── Helpers ───────────────────────────────────────────────────────────────

function Spinner() {
  return <div style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: 12 }}>Cargando...</div>;
}

function EmptyState({ icon, text }: { icon: string; text: string }) {
  return (
    <div style={{ padding: '40px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, color: 'var(--color-text-tertiary)' }}>
      <span style={{ fontSize: 32 }}>{icon}</span>
      <span style={{ fontSize: 13 }}>{text}</span>
    </div>
  );
}

function StatusBadge({ status }: { status: PlanStatus }) {
  const cfg = PLAN_STATUS[status] ?? PLAN_STATUS.pending;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 6, background: cfg.bg, color: cfg.color, fontSize: 10, fontWeight: 700 }}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

function formatDate(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function formatDuration(ms?: number) {
  if (!ms) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

const LABEL_STYLE: React.CSSProperties = {
  display: 'block', marginBottom: 6, fontSize: 11, fontWeight: 700,
  color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em',
};
const INPUT_STYLE: React.CSSProperties = {
  width: '100%', padding: '9px 12px', borderRadius: 8,
  border: '1px solid var(--color-border-secondary)',
  background: 'var(--color-background-secondary)',
  color: 'var(--color-text-primary)', fontSize: 13,
  boxSizing: 'border-box', outline: 'none',
};

// ─── Plan List Item ─────────────────────────────────────────────────────────

function PlanItem({ plan, selected, onClick, onEdit, onDelete }: {
  plan: ExecutionPlan; selected: boolean;
  onClick: () => void; onEdit: () => void; onDelete: () => void;
}) {
  const canEditRole = useAppStore(s => s.currentUserRole) !== 'viewer';
  const cfg = PLAN_STATUS[plan.status] ?? PLAN_STATUS.pending;
  const isLive = LIVE_STATUSES.includes(plan.status);
  const canEdit = EDITABLE_STATUSES.includes(plan.status);
  const canDelete = DELETABLE_STATUSES.includes(plan.status);

  return (
    <div onClick={onClick} style={{
      padding: '12px 14px', borderRadius: 10, cursor: 'pointer', marginBottom: 6,
      border: `1px solid ${selected ? '#3B8BD4' : isLive ? '#B5D4F4' : 'var(--color-border-tertiary)'}`,
      background: selected ? '#E6F1FB22' : 'var(--color-background-primary)',
      transition: 'all 0.15s',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {plan.name}
        </span>
        <StatusBadge status={plan.status} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 10, fontSize: 11, color: 'var(--color-text-tertiary)' }}>
          <span>{plan.case_ids?.length ?? 0} caso{(plan.case_ids?.length ?? 0) !== 1 ? 's' : ''}</span>
          <span>{plan.environment}</span>
          {plan.version_label && (
            <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{plan.version_label}</span>
          )}
          <span>{formatDate(plan.created_at)}</span>
        </div>
        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
          {canEditRole && canEdit && (
            <button onClick={onEdit} title="Editar plan"
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--color-text-tertiary)', padding: '2px 4px', borderRadius: 4 }}>
              ✎
            </button>
          )}
          {canEditRole && canDelete && (
            <button onClick={onDelete} title="Eliminar plan"
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--color-text-tertiary)', padding: '2px 4px', borderRadius: 4 }}>
              ✕
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Case Selector Tree ─────────────────────────────────────────────────────

function CaseSelector({ selectedIds, onToggle, onSelectAll }: {
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onSelectAll: (ids: string[]) => void;
}) {
  const [systems, setSystems] = useState<TestSystem[]>([]);
  const [versionsMap, setVersionsMap] = useState<Record<string, Version[]>>({});
  const [selectedVersion, setSelectedVersion] = useState<Record<string, string>>({});
  const [reqs, setReqs] = useState<Record<string, Requirement[]>>({});
  const [scenariosMap, setScenariosMap] = useState<Record<string, Scenario[]>>({});
  const [cases, setCases] = useState<Record<string, TestCase[]>>({});
  const [expandedSys, setExpandedSys] = useState<Set<string>>(new Set());
  const [expandedReq, setExpandedReq] = useState<Set<string>>(new Set());
  const [expandedScen, setExpandedScen] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => { systemsApi.list().then(s => { setSystems(s); setLoading(false); }); }, []);

  // Carga las versiones de un sistema y resuelve cuál usar por defecto
  // (la activa, o la primera si ninguna lo está) sin pisar una elección previa del usuario.
  const loadVersions = async (sysId: string): Promise<string | undefined> => {
    let list = versionsMap[sysId];
    if (!list) {
      list = await versionsApi.list(sysId);
      setVersionsMap(prev => ({ ...prev, [sysId]: list! }));
    }
    const current = selectedVersion[sysId];
    if (current && list.some(v => v.id === current)) return current;
    const def = list.find(v => v.status === 'active')?.id ?? list[0]?.id;
    if (def) setSelectedVersion(prev => ({ ...prev, [sysId]: def }));
    return def;
  };
  const loadReqs = async (versionId: string) => {
    if (reqs[versionId]) return;
    const r = await reqApi.list(versionId);
    setReqs(prev => ({ ...prev, [versionId]: r }));
  };
  const loadScenarios = async (reqId: string) => {
    if (scenariosMap[reqId]) return;
    const s = await scenariosApi.list(reqId);
    setScenariosMap(prev => ({ ...prev, [reqId]: s }));
  };
  const loadCases = async (scenId: string) => {
    if (cases[scenId]) return;
    const c = await casesApi.list(scenId);
    setCases(prev => ({ ...prev, [scenId]: c }));
  };

  const toggleSys = async (id: string) => {
    const versionId = await loadVersions(id);
    if (versionId) await loadReqs(versionId);
    setExpandedSys(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const changeVersion = async (sysId: string, versionId: string) => {
    setSelectedVersion(prev => ({ ...prev, [sysId]: versionId }));
    await loadReqs(versionId);
  };
  const toggleReq = async (id: string) => { await loadScenarios(id); setExpandedReq(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const toggleScen = async (id: string) => { await loadCases(id); setExpandedScen(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); };

  const scenIds = (scenId: string) => (cases[scenId] ?? []).map(c => c.id);
  const scenAll = (scenId: string) => { const ids = scenIds(scenId); return ids.length > 0 && ids.every(id => selectedIds.has(id)); };
  const scenSome = (scenId: string) => scenIds(scenId).some(id => selectedIds.has(id)) && !scenAll(scenId);

  if (loading) return <Spinner />;
  if (!systems.length) return <EmptyState icon="⬡" text="No hay sistemas definidos" />;

  const arrowStyle = (expanded: boolean): React.CSSProperties => ({
    fontSize: 10, color: 'var(--color-text-tertiary)', display: 'inline-block',
    transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s', cursor: 'pointer',
  });

  return (
    <div style={{ fontSize: 12 }}>
      {systems.map(sys => (
        <div key={sys.id} style={{ marginBottom: 4 }}>
          <div onClick={() => toggleSys(sys.id)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6, cursor: 'pointer', background: 'var(--color-background-secondary)' }}>
            <span style={arrowStyle(expandedSys.has(sys.id))}>▶</span>
            <span style={{ fontSize: 14 }}>⬡</span>
            <span style={{ flex: 1, fontWeight: 600, color: 'var(--color-text-primary)' }}>{sys.name}</span>
          </div>
          {expandedSys.has(sys.id) && (
            <div style={{ marginLeft: 16, marginTop: 2 }}>
              {(versionsMap[sys.id]?.length ?? 0) > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px 8px' }}>
                  <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Versión</span>
                  <select
                    value={selectedVersion[sys.id] ?? ''}
                    onChange={e => changeVersion(sys.id, e.target.value)}
                    onClick={e => e.stopPropagation()}
                    style={{ fontSize: 11, padding: '3px 6px', borderRadius: 6, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)', color: 'var(--color-text-primary)' }}
                  >
                    {(versionsMap[sys.id] ?? []).map(v => (
                      <option key={v.id} value={v.id}>
                        {v.name} · {VERSION_STATUS_CFG[v.status]?.label ?? v.status}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {(reqs[selectedVersion[sys.id] ?? ''] ?? []).map(req => (
                <div key={req.id} style={{ marginBottom: 2 }}>
                  <div onClick={() => toggleReq(req.id)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', borderRadius: 6, cursor: 'pointer' }}>
                    <span style={arrowStyle(expandedReq.has(req.id))}>▶</span>
                    <span style={{ flex: 1, color: 'var(--color-text-primary)', fontWeight: 500 }}>{req.title}</span>
                  </div>
                  {expandedReq.has(req.id) && (
                    <div style={{ marginLeft: 16, marginTop: 2 }}>
                      {(scenariosMap[req.id] ?? []).map(sc => (
                        <div key={sc.id} style={{ marginBottom: 2 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', borderRadius: 6 }}>
                            <div onClick={() => { const ids = scenIds(sc.id); scenAll(sc.id) ? ids.forEach(id => onToggle(id)) : onSelectAll(ids.filter(id => !selectedIds.has(id))); }}
                              style={{ width: 14, height: 14, borderRadius: 3, border: `2px solid ${scenAll(sc.id) || scenSome(sc.id) ? '#3B8BD4' : 'var(--color-border-secondary)'}`, background: scenAll(sc.id) ? '#3B8BD4' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' }}>
                              {scenAll(sc.id) && <span style={{ color: '#fff', fontSize: 8 }}>✓</span>}
                              {scenSome(sc.id) && <span style={{ color: '#3B8BD4', fontSize: 10 }}>—</span>}
                            </div>
                            <span style={arrowStyle(expandedScen.has(sc.id))} onClick={() => toggleScen(sc.id)}>▶</span>
                            <span onClick={() => toggleScen(sc.id)} style={{ flex: 1, color: 'var(--color-text-secondary)', cursor: 'pointer' }}>◈ {sc.title}</span>
                            {scenIds(sc.id).filter(id => selectedIds.has(id)).length > 0 && (
                              <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>
                                {scenIds(sc.id).filter(id => selectedIds.has(id)).length}/{scenIds(sc.id).length}
                              </span>
                            )}
                          </div>
                          {expandedScen.has(sc.id) && (
                            <div style={{ marginLeft: 28, marginTop: 2 }}>
                              {(cases[sc.id] ?? []).length === 0
                                ? <div style={{ padding: '3px 8px', color: 'var(--color-text-tertiary)', fontSize: 11 }}>Sin casos</div>
                                : (cases[sc.id] ?? []).map(tc => (
                                  <div key={tc.id} onClick={() => onToggle(tc.id)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', borderRadius: 6, cursor: 'pointer', marginBottom: 1 }}>
                                    <div style={{ width: 14, height: 14, borderRadius: 3, border: `2px solid ${selectedIds.has(tc.id) ? '#3B8BD4' : 'var(--color-border-secondary)'}`, background: selectedIds.has(tc.id) ? '#3B8BD4' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                      {selectedIds.has(tc.id) && <span style={{ color: '#fff', fontSize: 8 }}>✓</span>}
                                    </div>
                                    <span style={{ flex: 1, color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tc.title}</span>
                                    {tc.generated_by === 'ai' && <span style={{ fontSize: 8, padding: '1px 4px', borderRadius: 3, background: '#EEEDFE', color: '#534AB7', fontWeight: 700 }}>IA</span>}
                                    {tc.has_dataset && <span title="Tiene dataset asignado" style={{ fontSize: 8, padding: '1px 4px', borderRadius: 3, background: '#e8f4fd', color: '#185FA5', fontWeight: 700 }}>📊</span>}
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Case Picker Modal (selección jerárquica en ventana aparte) ────────────

function CasePickerModal({ selectedIds, onToggle, onSelectAll, onClose }: {
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onSelectAll: (ids: string[]) => void;
  onClose: () => void;
}) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--color-background-primary)', borderRadius: 14, width: 520, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.18)', border: '1px solid var(--color-border-secondary)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text-primary)' }}>Agregar casos de prueba</div>
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{selectedIds.size} seleccionado{selectedIds.size !== 1 ? 's' : ''}</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', color: 'var(--color-text-tertiary)' }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
          <CaseSelector selectedIds={selectedIds} onToggle={onToggle} onSelectAll={onSelectAll} />
        </div>
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--color-border-tertiary)', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose}
            style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: '#3B8BD4', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
            ✓ Listo
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Selected Cases Table (orden via drag & drop) ──────────────────────────

function SortableCaseRow({ id, index, testCase, disabled, onRemove }: {
  id: string; index: number; testCase?: TestCase; disabled: boolean; onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };
  const crit = testCase ? (CRITICALITY_CFG[testCase.criticality] ?? CRITICALITY_CFG.medium) : null;

  return (
    <tr ref={setNodeRef} style={style}>
      <td style={{ padding: '8px 6px', width: 28, textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: 11 }}>{index + 1}</td>
      <td style={{ padding: '8px 6px', width: 24 }}>
        {!disabled && (
          <span {...attributes} {...listeners} style={{ cursor: 'grab', color: 'var(--color-text-tertiary)', fontSize: 14, touchAction: 'none' }} title="Arrastrar para reordenar">⠿</span>
        )}
      </td>
      <td style={{ padding: '8px 6px', fontSize: 12, color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0 }}>
        {testCase?.title ?? <span style={{ color: 'var(--color-text-tertiary)' }}>Cargando…</span>}
      </td>
      <td style={{ padding: '8px 6px', width: 80 }}>
        {crit && (
          <span style={{ padding: '2px 7px', borderRadius: 6, background: crit.bg, color: crit.color, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>
            {crit.label}
          </span>
        )}
      </td>
      <td style={{ padding: '8px 6px', width: 70 }}>
        <div style={{ display: 'flex', gap: 3 }}>
          {testCase?.generated_by === 'ai' && <span style={{ fontSize: 8, padding: '1px 4px', borderRadius: 3, background: '#EEEDFE', color: '#534AB7', fontWeight: 700 }}>IA</span>}
          {testCase?.has_dataset && <span title="Tiene dataset asignado" style={{ fontSize: 8, padding: '1px 4px', borderRadius: 3, background: '#e8f4fd', color: '#185FA5', fontWeight: 700 }}>📊</span>}
        </div>
      </td>
      <td style={{ padding: '8px 6px', width: 28 }}>
        {!disabled && (
          <button onClick={onRemove} title="Quitar del plan"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12, color: 'var(--color-text-tertiary)', padding: '2px 4px', borderRadius: 4 }}>
            ✕
          </button>
        )}
      </td>
    </tr>
  );
}

function SelectedCasesTable({ caseOrder, caseMeta, disabled, onReorder, onRemove }: {
  caseOrder: string[];
  caseMeta: Record<string, TestCase>;
  disabled: boolean;
  onReorder: (next: string[]) => void;
  onRemove: (id: string) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = caseOrder.indexOf(active.id as string);
    const newIdx = caseOrder.indexOf(over.id as string);
    if (oldIdx === -1 || newIdx === -1) return;
    onReorder(arrayMove(caseOrder, oldIdx, newIdx));
  }, [caseOrder, onReorder]);

  if (caseOrder.length === 0) {
    return <EmptyState icon="▤" text="Sin casos agregados aún" />;
  }

  return (
    <div style={{ border: '1px solid var(--color-border-secondary)', borderRadius: 10, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--color-background-secondary)' }}>
            <th style={{ padding: '7px 6px', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-text-tertiary)', textAlign: 'center' }}>#</th>
            <th style={{ padding: '7px 6px', width: 24 }}></th>
            <th style={{ padding: '7px 6px', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-text-tertiary)', textAlign: 'left' }}>Caso de prueba</th>
            <th style={{ padding: '7px 6px', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-text-tertiary)', textAlign: 'left' }}>Criticidad</th>
            <th style={{ padding: '7px 6px', width: 50 }}></th>
            <th style={{ padding: '7px 6px', width: 28 }}></th>
          </tr>
        </thead>
        <tbody>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={caseOrder} strategy={verticalListSortingStrategy}>
              {caseOrder.map((id, i) => (
                <SortableCaseRow key={id} id={id} index={i} testCase={caseMeta[id]} disabled={disabled} onRemove={() => onRemove(id)} />
              ))}
            </SortableContext>
          </DndContext>
        </tbody>
      </table>
    </div>
  );
}

// ─── Plan Form (Create & Edit) ──────────────────────────────────────────────

interface PlanFormProps {
  initial?: ExecutionPlan;           // si viene → modo edición
  onSaved: (plan: ExecutionPlan) => void;
  onCancel: () => void;
}

function PlanForm({ initial, onSaved, onCancel }: PlanFormProps) {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name ?? `Plan ${new Date().toLocaleDateString('es')}`);
  const [environment, setEnvironment] = useState(initial?.environment ?? 'staging');
  const [browser, setBrowser] = useState((initial?.config?.browser as string) ?? 'chromium');
  const [caseOrder, setCaseOrder] = useState<string[]>(initial?.case_ids ?? []);
  const [caseMeta, setCaseMeta] = useState<Record<string, TestCase>>({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const caseMetaRef = useRef(caseMeta);
  const [preferredAgentId, setPreferredAgentId] = useState<string>(initial?.preferred_agent_id ?? '');
  const [agents, setAgents] = useState<Agent[]>([]);
  // Scheduling
  const [isScheduled, setIsScheduled] = useState<boolean>(initial?.is_scheduled ?? false);
  const [scheduleType, setScheduleType] = useState<'once' | 'recurrent'>(initial?.recurrence ? 'recurrent' : 'once');
  const [scheduledAt, setScheduledAt] = useState<string>(initial?.scheduled_at ? initial.scheduled_at.slice(0,16) : '');
  const [recurrenceDays, setRecurrenceDays] = useState<number[]>(initial?.recurrence?.days ?? [1,2,3,4,5]);
  const [recurrenceTime, setRecurrenceTime] = useState<string>(initial?.recurrence?.time ?? '07:00');
  const [scheduleTz, setScheduleTz] = useState<string>(initial?.schedule_tz ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [checkingDatasets, setCheckingDatasets] = useState(false);
  const [zeroActiveConfirm, setZeroActiveConfirm] = useState<{ caseTitles: string[] } | null>(null);

  useEffect(() => {
    agentsApi.list().then(setAgents).catch(() => setAgents([]));
  }, []);

  useEffect(() => { caseMetaRef.current = caseMeta; }, [caseMeta]);

  // Carga metadata (título, criticidad, origen) de los casos seleccionados para mostrarlos en la tabla
  useEffect(() => {
    const missing = caseOrder.filter(id => !caseMetaRef.current[id]);
    if (missing.length === 0) return;
    let cancelled = false;
    Promise.all(missing.map(id => casesApi.get(id).catch(() => null))).then(results => {
      if (cancelled) return;
      setCaseMeta(prev => {
        const next = { ...prev };
        missing.forEach((id, i) => { if (results[i]) next[id] = results[i] as TestCase; });
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [caseOrder]);

  const selectedIds = useMemo(() => new Set(caseOrder), [caseOrder]);
  const toggleCase = (id: string) => setCaseOrder(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const selectAll = (ids: string[]) => setCaseOrder(prev => [...prev, ...ids.filter(id => !prev.includes(id))]);
  const removeCase = (id: string) => setCaseOrder(prev => prev.filter(x => x !== id));

  // Casos con dataset asignado cuyo escenario no tiene ninguna fila activa: la ejecución
  // caerá a los valores "duros" de la definición de acciones en vez de data-driven.
  const findCasesWithZeroActiveDataset = async (): Promise<string[]> => {
    const datasetCases = caseOrder.map(id => caseMetaRef.current[id]).filter(tc => tc?.has_dataset) as TestCase[];
    if (datasetCases.length === 0) return [];
    const scenarioIds = Array.from(new Set(datasetCases.map(tc => tc.scenario_id)));
    const results = await Promise.all(
      scenarioIds.map(sid => datasetApi.getDataset(sid).catch(() => null))
    );
    const zeroActiveScenarioIds = new Set(
      scenarioIds.filter((sid, i) => {
        const ds = results[i] as { active_row_count?: number } | null;
        return ds && (ds.active_row_count ?? 0) === 0;
      })
    );
    return datasetCases.filter(tc => zeroActiveScenarioIds.has(tc.scenario_id)).map(tc => tc.title);
  };

  const handleSave = async () => {
    if (!name.trim()) { setError('El nombre es requerido'); return; }
    if (caseOrder.length === 0) { setError('Selecciona al menos un caso'); return; }
    setError('');
    setCheckingDatasets(true);
    const caseTitles = await findCasesWithZeroActiveDataset().finally(() => setCheckingDatasets(false));
    if (caseTitles.length > 0) { setZeroActiveConfirm({ caseTitles }); return; }
    await persistPlan();
  };

  const persistPlan = async () => {
    setSaving(true); setError('');
    try {
      const payload = {
        name: name.trim(),
        environment,
        case_ids: caseOrder,
        config: { browser },
        preferred_agent_id: preferredAgentId || null,
        is_scheduled: isScheduled,
        scheduled_at: isScheduled && scheduleType === 'once' && scheduledAt
          ? new Date(scheduledAt).toISOString() : null,
        recurrence: isScheduled && scheduleType === 'recurrent'
          ? { days: recurrenceDays, time: recurrenceTime } : null,
        schedule_tz: isScheduled ? scheduleTz : 'UTC',
      };
      let plan: ExecutionPlan;
      if (isEdit && initial) {
        plan = await executionsApi.updatePlan(initial.id, payload);
      } else {
        plan = await executionsApi.createPlan(payload);
      }
      onSaved(plan);
    } catch (e: unknown) {
      setError((e as Error).message ?? 'Error guardando el plan');
    } finally {
      setSaving(false);
    }
  };

  const canEditCases = !isEdit || EDITABLE_STATUSES.includes(initial?.status ?? 'pending');

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text-primary)' }}>
            {isEdit ? `Editar plan` : 'Nuevo plan de ejecución'}
          </div>
          {isEdit && <StatusBadge status={initial!.status} />}
        </div>
        <button onClick={onCancel} style={{ border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', color: 'var(--color-text-tertiary)' }}>✕</button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Config */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={LABEL_STYLE}>Nombre *</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Ej: Sprint 3 - Smoke Tests" style={INPUT_STYLE} />
          </div>
          <div>
            <label style={LABEL_STYLE}>Ambiente</label>
            <select value={environment} onChange={e => setEnvironment(e.target.value)} style={INPUT_STYLE}>
              <option value="staging">Staging</option>
              <option value="production">Production</option>
              <option value="development">Development</option>
              <option value="qa">QA</option>
            </select>
          </div>
          <div>
            <label style={LABEL_STYLE}>Browser</label>
            <select value={browser} onChange={e => setBrowser(e.target.value)} style={INPUT_STYLE}>
              <option value="chromium">Chromium</option>
              <option value="firefox">Firefox</option>
              <option value="webkit">WebKit (Safari)</option>
            </select>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={LABEL_STYLE}>Agente preferido</label>
            <select
              value={preferredAgentId}
              onChange={e => setPreferredAgentId(e.target.value)}
              style={INPUT_STYLE}
            >
              <option value="">Cualquier agente disponible</option>
              {agents.map(a => {
                const st = a.last_heartbeat && Date.now() - new Date(a.last_heartbeat).getTime() < 120000
                  ? a.status : 'offline';
                return (
                  <option key={a.id} value={a.id} disabled={st === 'offline'}>
                    {a.name} — {a.platform} {st === 'busy' ? '● ejecutando' : st === 'online' ? '● online' : '(offline)'}
                  </option>
                );
              })}
            </select>
            <div style={{ marginTop: 4, fontSize: 11, color: 'var(--color-text-tertiary)' }}>
              Si seleccionas un agente, solo ese agente procesará los runs de este plan.
            </div>
          </div>

          {/* Scheduling */}
          <div style={{ gridColumn: '1 / -1', padding: '14px 16px', borderRadius: 10, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: isScheduled ? 14 : 0 }}>
              <div onClick={() => setIsScheduled(v => !v)}
                style={{ width: 36, height: 20, borderRadius: 10, background: isScheduled ? '#3B8BD4' : 'var(--color-border-secondary)', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: isScheduled ? 18 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>Calendarizar ejecución</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                  {isScheduled ? 'Define cuándo se ejecutará este plan' : 'Sin calendarización — se ejecuta inmediatamente cuando haya un agente libre'}
                </div>
              </div>
            </div>

            {isScheduled && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* Tipo: una vez / recurrente */}
                <div style={{ display: 'flex', gap: 8 }}>
                  {(['once', 'recurrent'] as const).map(t => (
                    <button key={t} onClick={() => setScheduleType(t)}
                      style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: '1px solid', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
                        borderColor: scheduleType === t ? '#3B8BD4' : 'var(--color-border-secondary)',
                        background: scheduleType === t ? '#EBF0FC' : 'transparent',
                        color: scheduleType === t ? '#185FA5' : 'var(--color-text-secondary)' }}>
                      {t === 'once' ? '📅 Una vez' : '🔁 Recurrente'}
                    </button>
                  ))}
                </div>

                {scheduleType === 'once' && (
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Fecha y hora</label>
                    <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)}
                      style={{ width: '100%', padding: '7px 10px', fontSize: 12, borderRadius: 7, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-primary)', color: 'var(--color-text-primary)', outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                )}

                {scheduleType === 'recurrent' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 6 }}>Días de la semana</label>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {['L','M','X','J','V','S','D'].map((d, i) => {
                          const selected = recurrenceDays.includes(i);
                          return (
                            <button key={i} onClick={() => setRecurrenceDays(prev => selected ? prev.filter(x => x !== i) : [...prev, i].sort())}
                              style={{ width: 34, height: 34, borderRadius: '50%', border: '1px solid', fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s',
                                borderColor: selected ? '#3B8BD4' : 'var(--color-border-secondary)',
                                background: selected ? '#3B8BD4' : 'transparent',
                                color: selected ? '#fff' : 'var(--color-text-secondary)' }}>
                              {d}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Hora</label>
                        <input type="time" value={recurrenceTime} onChange={e => setRecurrenceTime(e.target.value)}
                          style={{ width: '100%', padding: '7px 10px', fontSize: 12, borderRadius: 7, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-primary)', color: 'var(--color-text-primary)', outline: 'none', boxSizing: 'border-box' }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Zona horaria</label>
                        <input value={scheduleTz} onChange={e => setScheduleTz(e.target.value)}
                          style={{ width: '100%', padding: '7px 10px', fontSize: 12, borderRadius: 7, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-primary)', color: 'var(--color-text-primary)', outline: 'none', boxSizing: 'border-box' }}
                          placeholder="America/Mexico_City" />
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', padding: '6px 10px', borderRadius: 6, background: 'var(--color-background-primary)' }}>
                  {scheduleType === 'once' && scheduledAt
                    ? `Se ejecutará el ${new Date(scheduledAt).toLocaleString('es')}`
                    : scheduleType === 'recurrent' && recurrenceDays.length > 0
                    ? `Se ejecutará cada semana los días seleccionados a las ${recurrenceTime} (${scheduleTz})`
                    : 'Configura la programación arriba'}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Cases */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <label style={LABEL_STYLE}>Casos de prueba * — orden de ejecución</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 11, color: caseOrder.length > 0 ? '#3B8BD4' : 'var(--color-text-tertiary)', fontWeight: 600 }}>
                {caseOrder.length} seleccionado{caseOrder.length !== 1 ? 's' : ''}
              </span>
              {canEditCases && (
                <button onClick={() => setPickerOpen(true)}
                  style={{ border: 'none', background: '#3B8BD4', color: '#fff', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
                  + Agregar casos
                </button>
              )}
            </div>
          </div>
          <SelectedCasesTable caseOrder={caseOrder} caseMeta={caseMeta} disabled={!canEditCases} onReorder={setCaseOrder} onRemove={removeCase} />
          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--color-text-tertiary)' }}>
            {canEditCases ? 'Arrastra el ⠿ para definir el orden en que se ejecutarán los casos.' : `⚠ Los casos no se pueden modificar en un plan con estado "${initial?.status}"`}
          </div>
        </div>

        {pickerOpen && (
          <CasePickerModal selectedIds={selectedIds} onToggle={toggleCase} onSelectAll={selectAll} onClose={() => setPickerOpen(false)} />
        )}

        {error && (
          <div style={{ padding: '10px 14px', borderRadius: 8, background: '#FCEBEB', border: '1px solid #F7C1C1', color: '#791F1F', fontSize: 12 }}>
            {error}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '14px 20px', borderTop: '1px solid var(--color-border-tertiary)', display: 'flex', gap: 10, justifyContent: 'flex-end', background: 'var(--color-background-primary)' }}>
        <button onClick={onCancel}
          style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid var(--color-border-secondary)', background: 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--color-text-secondary)', fontWeight: 500 }}>
          Cancelar
        </button>
        <button onClick={handleSave} disabled={saving || checkingDatasets || caseOrder.length === 0}
          style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: saving || checkingDatasets || caseOrder.length === 0 ? 'var(--color-border-secondary)' : '#3B8BD4', color: '#fff', cursor: saving || checkingDatasets || caseOrder.length === 0 ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700 }}>
          {saving ? '⟳ Guardando...' : checkingDatasets ? '⟳ Verificando datasets...' : isEdit ? '✓ Guardar cambios' : `▶ Crear plan (${caseOrder.length} casos)`}
        </button>
      </div>

      {zeroActiveConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ background: 'var(--color-background-primary)', borderRadius: 14, width: 460, boxShadow: '0 12px 40px rgba(0,0,0,0.18)', border: '1px solid var(--color-border-secondary)' }}>
            <div style={{ padding: '18px 20px 0' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: 8 }}>⚠ Dataset sin registros activos</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
                Los siguientes casos tienen un dataset asignado, pero ninguna fila está marcada como activa. Se ejecutarán con los valores fijos definidos en las acciones (sin Data Driven):
              </div>
              <ul style={{ margin: '10px 0', paddingLeft: 20, fontSize: 12, color: 'var(--color-text-primary)' }}>
                {zeroActiveConfirm.caseTitles.map(t => <li key={t} style={{ marginBottom: 4 }}>{t}</li>)}
              </ul>
            </div>
            <div style={{ padding: '14px 20px', display: 'flex', gap: 10, justifyContent: 'flex-end', borderTop: '1px solid var(--color-border-tertiary)', marginTop: 8 }}>
              <button onClick={() => setZeroActiveConfirm(null)}
                style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid var(--color-border-secondary)', background: 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--color-text-secondary)', fontWeight: 500 }}>
                Cancelar
              </button>
              <button onClick={() => { setZeroActiveConfirm(null); persistPlan(); }}
                style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#854F0B', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                Continuar con datos fijos
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Plan History Panel ─────────────────────────────────────────────────────

const RUN_STATUS_CFG: Record<string, { color: string; bg: string; icon: string }> = {
  pending: { color: '#5F5E5A', bg: '#F1EFE8', icon: '○' },
  running: { color: '#185FA5', bg: '#E6F1FB', icon: '◉' },
  passed:  { color: '#0F6E56', bg: '#E1F5EE', icon: '✓' },
  failed:  { color: '#A32D2D', bg: '#FCEBEB', icon: '✗' },
  skipped: { color: '#854F0B', bg: '#FAEEDA', icon: '⊘' },
  error:   { color: '#793C1D', bg: '#FAECE7', icon: '!' },
  cancelled: { color: '#5F5E5A', bg: '#F1EFE8', icon: '⊘' },
};

function RunCard({ run }: { run: ExecutionRun }) {
  const sc = RUN_STATUS_CFG[run.status] ?? RUN_STATUS_CFG.pending;
  const pct = run.total_steps > 0 ? Math.round((run.passed_steps + run.failed_steps) / run.total_steps * 100) : 0;
  return (
    <div style={{ padding: '12px 14px', borderRadius: 10, border: `1px solid ${run.status === 'failed' ? '#FCEBEB' : 'var(--color-border-tertiary)'}`, background: 'var(--color-background-secondary)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span style={{ padding: '2px 8px', borderRadius: 6, background: sc.bg, color: sc.color, fontSize: 10, fontWeight: 700 }}>
          {sc.icon} {run.status}
        </span>
        <span style={{ flex: 1, fontSize: 12, color: 'var(--color-text-secondary)', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {run.case_id?.slice(0, 8)}...
        </span>
        <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{formatDuration(run.duration_ms ?? undefined)}</span>
      </div>
      <div style={{ height: 4, borderRadius: 2, background: 'var(--color-border-tertiary)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', height: '100%' }}>
          <div style={{ width: `${run.total_steps > 0 ? run.passed_steps / run.total_steps * 100 : 0}%`, background: '#1D9E75' }} />
          <div style={{ width: `${run.total_steps > 0 ? run.failed_steps / run.total_steps * 100 : 0}%`, background: '#E24B4A' }} />
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: 'var(--color-text-tertiary)' }}>
        <span>{run.passed_steps} ✓ / {run.failed_steps} ✗ / {run.total_steps} total</span>
        <span>{pct}%</span>
      </div>
      {run.error_summary && (
        <div style={{ marginTop: 6, padding: '6px 10px', borderRadius: 6, background: '#FCEBEB', color: '#791F1F', fontSize: 11 }}>
          {run.error_summary}
        </div>
      )}
    </div>
  );
}

function AttemptGroup({ attempt, runs, isLatest, expanded, onToggle, onEvidence }: {
  attempt: number; runs: ExecutionRun[]; isLatest: boolean; expanded: boolean; onToggle: () => void; onEvidence: () => void;
}) {
  const passed = runs.filter(r => r.status === 'passed').length;
  const failed = runs.filter(r => r.status === 'failed' || r.status === 'error').length;
  const firstRun = runs[0];

  return (
    <div style={{ border: '1px solid var(--color-border-tertiary)', borderRadius: 10, overflow: 'hidden' }}>
      <div onClick={onToggle} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', background: 'var(--color-background-secondary)' }}>
        <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)', transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s', display: 'inline-block' }}>▶</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-primary)' }}>
          Ejecución #{attempt}{isLatest ? ' (vigente)' : ''}
        </span>
        <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{formatDate(firstRun?.started_at ?? firstRun?.finished_at)}</span>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: '#0F6E56' }}>{passed} ✓</span>
        <span style={{ fontSize: 11, color: '#A32D2D' }}>{failed} ✗</span>
        <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{runs.length} runs</span>
        <button onClick={e => { e.stopPropagation(); onEvidence(); }}
          style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid var(--color-border-secondary)', background: 'transparent', cursor: 'pointer', fontSize: 11, color: 'var(--color-text-secondary)', fontWeight: 600 }}>
          📄 Evidencias
        </button>
      </div>
      {expanded && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 10 }}>
          {runs.map(run => <RunCard key={run.id} run={run} />)}
        </div>
      )}
    </div>
  );
}

function PlanHistoryPanel({ plan, onClose, onRerun, onEdit, onEvidence }: {
  plan: ExecutionPlan; onClose: () => void; onRerun: () => void; onEdit: () => void;
  onEvidence: (runs?: ExecutionRun[], attempt?: number) => void;
}) {
  const [runs, setRuns] = useState<ExecutionRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedAttempts, setExpandedAttempts] = useState<Set<number>>(new Set());
  const canEdit = EDITABLE_STATUSES.includes(plan.status);
  const canEditRole = useAppStore(s => s.currentUserRole) !== 'viewer';

  useEffect(() => {
    setLoading(true);
    executionsApi.getRuns(plan.id, { all: true }).then(all => {
      setRuns(all);
      const latestAttempt = Math.max(1, ...all.map(r => r.attempt ?? 1));
      setExpandedAttempts(new Set([latestAttempt]));
    }).finally(() => setLoading(false));
  }, [plan.id]);

  // Agrupa por intento (más reciente primero) para no mezclar re-ejecuciones
  const attemptGroups = useMemo(() => {
    const byAttempt = new Map<number, ExecutionRun[]>();
    for (const run of runs) {
      const a = run.attempt ?? 1;
      if (!byAttempt.has(a)) byAttempt.set(a, []);
      byAttempt.get(a)!.push(run);
    }
    return Array.from(byAttempt.entries()).sort((a, b) => b[0] - a[0]);
  }, [runs]);
  const latestAttempt = attemptGroups[0]?.[0] ?? 1;
  const latestRuns = attemptGroups[0]?.[1] ?? [];

  const toggleAttempt = (a: number) => setExpandedAttempts(prev => {
    const n = new Set(prev); n.has(a) ? n.delete(a) : n.add(a); return n;
  });

  // Resumen superior: siempre referido al intento vigente (coincide con plan.status)
  const passedCount = latestRuns.filter(r => r.status === 'passed').length;
  const failedCount = latestRuns.filter(r => r.status === 'failed').length;
  const passRate = latestRuns.length > 0 ? Math.round(passedCount / latestRuns.length * 100) : 0;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: 4 }}>{plan.name}</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <StatusBadge status={plan.status} />
            <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{plan.environment}</span>
            <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{plan.case_ids?.length ?? 0} casos</span>
            {plan.version_label && (
              <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600, background: 'color-mix(in srgb, var(--accent) 10%, transparent)', padding: '1px 7px', borderRadius: 10 }}>
                {plan.version_label}
              </span>
            )}
            {plan.preferred_agent_id && (
              <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>◉ agente asignado</span>
            )}
            {plan.is_scheduled && (
              <span style={{ fontSize: 11, color: '#185FA5', fontWeight: 600 }}>
                {plan.recurrence ? '🔁 recurrente' : '📅 calendarizado'}
              </span>
            )}
            <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{formatDate(plan.created_at)}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button onClick={() => onEvidence()}
            style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#F47920', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            📄 Ver evidencias
          </button>
          {canEdit && canEditRole && (
            <button onClick={onEdit}
              style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--color-border-secondary)', background: 'transparent', cursor: 'pointer', fontSize: 12, color: 'var(--color-text-secondary)', fontWeight: 500 }}>
              ✎ Editar
            </button>
          )}
          {!LIVE_STATUSES.includes(plan.status) && (
            <button onClick={onRerun}
              style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#3B8BD4', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              ↺ Re-ejecutar
            </button>
          )}
          {LIVE_STATUSES.includes(plan.status) && (
            <button onClick={async () => { await executionsApi.cancelPlan(plan.id); onClose(); }}
              style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #F7C1C1', background: 'transparent', cursor: 'pointer', fontSize: 12, color: '#A32D2D' }}>
              Cancelar ejecución
            </button>
          )}
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', color: 'var(--color-text-tertiary)' }}>✕</button>
        </div>
      </div>

      {latestRuns.length > 0 && (
        <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', gap: 20 }}>
          {[
            { label: 'Total',    value: latestRuns.length, color: 'var(--color-text-primary)' },
            { label: 'Pasaron',  value: passedCount,   color: '#0F6E56' },
            { label: 'Fallaron', value: failedCount,   color: '#A32D2D' },
            { label: 'Pass rate', value: `${passRate}%`, color: passRate >= 80 ? '#0F6E56' : passRate >= 60 ? '#854F0B' : '#A32D2D' },
          ].map(k => (
            <div key={k.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: k.color }}>{k.value}</div>
              <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>{k.label}</div>
            </div>
          ))}
          {attemptGroups.length > 1 && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--color-text-primary)' }}>{attemptGroups.length}</div>
              <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>Ejecuciones</div>
            </div>
          )}
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
        {loading ? <Spinner />
        : runs.length === 0 ? <EmptyState icon="◎" text="Sin ejecuciones registradas" />
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {attemptGroups.map(([attempt, attemptRuns]) => (
              <AttemptGroup
                key={attempt}
                attempt={attempt}
                runs={attemptRuns}
                isLatest={attempt === latestAttempt}
                expanded={expandedAttempts.has(attempt)}
                onToggle={() => toggleAttempt(attempt)}
                onEvidence={() => onEvidence(attemptRuns, attempt)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main ExecutionsModule ──────────────────────────────────────────────────

type RightPanel = 'none' | 'create' | 'edit' | 'monitor' | 'history';

export function ExecutionsModule() {
  const [plans, setPlans] = useState<ExecutionPlan[]>([]);
  const [evidenceView, setEvidenceView] = useState<
    { plan: ExecutionPlan; runs?: ExecutionRun[]; attempt?: number } | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<ExecutionPlan | null>(null);
  const [rightPanel, setRightPanel] = useState<RightPanel>('none');
  const [qPlans, setQPlans] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<ExecutionPlan | null>(null);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const p = await executionsApi.listPlans();
      p.sort((a, b) => {
        if (LIVE_STATUSES.includes(a.status) && !LIVE_STATUSES.includes(b.status)) return -1;
        if (LIVE_STATUSES.includes(b.status) && !LIVE_STATUSES.includes(a.status)) return 1;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      setPlans(p);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  // Auto-refresh if live plans exist
  useEffect(() => {
    if (!plans.some(p => LIVE_STATUSES.includes(p.status))) return;
    const t = setInterval(loadPlans, 10000);
    return () => clearInterval(t);
  }, [plans, loadPlans]);

  const handleSaved = async (plan: ExecutionPlan) => {
    await loadPlans();
    setSelectedPlan(plan);
    setRightPanel(LIVE_STATUSES.includes(plan.status) ? 'monitor' : 'history');
  };

  const handleSelectPlan = (plan: ExecutionPlan) => {
    setSelectedPlan(plan);
    setRightPanel(LIVE_STATUSES.includes(plan.status) ? 'monitor' : 'history');
  };

  const handleDelete = (plan: ExecutionPlan) => setConfirmDelete(plan);

  const handleDeleteConfirmed = async () => {
    if (!confirmDelete) return;
    try {
      await executionsApi.deletePlan(confirmDelete.id);
      if (selectedPlan?.id === confirmDelete.id) { setSelectedPlan(null); setRightPanel('none'); }
      await loadPlans();
    } catch (e: unknown) {
      console.error(e);
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleRerun = async () => {
    if (!selectedPlan) return;
    // Re-ejecuta sobre el MISMO plan (nuevo intento/attempt en backend) en vez
    // de crear un ExecutionPlan duplicado — el historial queda agrupado en el
    // detalle del plan (PlanHistoryPanel) en lugar de aparecer como registros aparte.
    const plan = await executionsApi.rerunPlan(selectedPlan.id);
    await loadPlans();
    setSelectedPlan(plan);
    setRightPanel('monitor');
  };

  const currentUserRole = useAppStore(s => s.currentUserRole);
  const runningCount = plans.filter(p => LIVE_STATUSES.includes(p.status)).length;
  const filteredPlans = qPlans
    ? plans.filter(p => p.name.toLowerCase().includes(qPlans.toLowerCase()) || p.environment.toLowerCase().includes(qPlans.toLowerCase()))
    : plans;

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 120px)', borderRadius: 14, border: '1px solid var(--color-border-tertiary)', overflow: 'hidden', background: 'var(--color-background-primary)' }}>

      {/* LEFT: Plans list */}
      <div style={{ width: 300, borderRight: '1px solid var(--color-border-tertiary)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid var(--color-border-tertiary)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Planes de ejecución
            </span>
            {currentUserRole !== 'viewer' && (
              <button onClick={() => { setSelectedPlan(null); setRightPanel('create'); }}
                style={{ border: 'none', background: '#3B8BD4', color: '#fff', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                + Nuevo
              </button>
            )}
          </div>
          {runningCount > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 6, background: '#E6F1FB', fontSize: 11, color: '#185FA5' }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#3B8BD4', animation: 'pulse 1.5s ease-in-out infinite' }} />
              {runningCount} ejecución{runningCount !== 1 ? 'es' : ''} activa{runningCount !== 1 ? 's' : ''}
            </div>
          )}
        </div>

        <div style={{ padding: '6px 10px 2px' }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: 'var(--color-text-tertiary)', pointerEvents: 'none' }}>🔍</span>
            <input
              value={qPlans}
              onChange={e => setQPlans(e.target.value)}
              placeholder="Buscar plan..."
              style={{ width: '100%', padding: '5px 24px 5px 24px', fontSize: 11, borderRadius: 6, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)', color: 'var(--color-text-primary)', outline: 'none', boxSizing: 'border-box' }}
            />
            {qPlans && <button onClick={() => setQPlans('')} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12, color: 'var(--color-text-tertiary)', padding: 0 }}>✕</button>}
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 10 }}>
          {loading ? <Spinner />
          : filteredPlans.length === 0 ? <EmptyState icon="▷" text={qPlans ? 'Sin resultados' : 'Sin planes de ejecución'} />
          : filteredPlans.map(plan => (
            <PlanItem key={plan.id} plan={plan}
              selected={selectedPlan?.id === plan.id}
              onClick={() => handleSelectPlan(plan)}
              onEdit={() => { setSelectedPlan(plan); setRightPanel('edit'); }}
              onDelete={() => handleDelete(plan)}
            />
          ))}
        </div>

        <div style={{ padding: '8px 10px', borderTop: '1px solid var(--color-border-tertiary)' }}>
          <button onClick={loadPlans}
            style={{ width: '100%', padding: '6px', borderRadius: 6, border: '1px solid var(--color-border-secondary)', background: 'transparent', cursor: 'pointer', fontSize: 11, color: 'var(--color-text-tertiary)' }}>
            ↺ Actualizar lista
          </button>
        </div>
      </div>

      {/* RIGHT: Contextual panel */}
      <div style={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
        {rightPanel === 'none' && (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-tertiary)', gap: 12 }}>
            <span style={{ fontSize: 48 }}>▷</span>
            <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-text-secondary)' }}>Selecciona un plan o crea uno nuevo</span>
            <button onClick={() => setRightPanel('create')}
              style={{ marginTop: 8, padding: '10px 24px', borderRadius: 10, border: 'none', background: '#3B8BD4', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
              + Crear plan de ejecución
            </button>
          </div>
        )}

        {(rightPanel === 'create' || rightPanel === 'edit') && (
          <PlanForm
            initial={rightPanel === 'edit' ? selectedPlan ?? undefined : undefined}
            onSaved={handleSaved}
            onCancel={() => setRightPanel(selectedPlan ? 'history' : 'none')}
          />
        )}

        {rightPanel === 'monitor' && selectedPlan && (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text-primary)' }}>{selectedPlan.name}</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>Monitor en tiempo real</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setRightPanel('history')}
                  style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid var(--color-border-secondary)', background: 'transparent', cursor: 'pointer', fontSize: 11, color: 'var(--color-text-secondary)' }}>
                  Ver histórico
                </button>
                <StatusBadge status={selectedPlan.status} />
              </div>
            </div>
            <div style={{ flex: 1, padding: '16px 20px', overflowY: 'auto' }}>
              <ExecutionMonitor planId={selectedPlan.id} />
            </div>
          </div>
        )}

        {rightPanel === 'history' && selectedPlan && (
          <PlanHistoryPanel
            plan={selectedPlan}
            onClose={() => setRightPanel('none')}
            onRerun={handleRerun}
            onEdit={() => setRightPanel('edit')}
            onEvidence={(runs, attempt) => setEvidenceView({ plan: selectedPlan, runs, attempt })}
          />
        )}
      </div>

      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>

      {evidenceView && (
        <EvidenceReport
          plan={evidenceView.plan}
          runs={evidenceView.runs}
          attempt={evidenceView.attempt}
          onClose={() => setEvidenceView(null)}
        />
      )}
      {/* Modal confirmación eliminar plan */}
      {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--color-background-primary)', borderRadius: 14, padding: '28px 28px 24px', width: 380, boxShadow: '0 12px 40px rgba(0,0,0,0.18)', border: '1px solid var(--color-border-secondary)' }}>
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 18 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: '#FCEBEB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>🗑</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: 4 }}>Eliminar plan</div>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  ¿Eliminar <strong>{confirmDelete!.name}</strong>? Se perderán todos sus runs y evidencias. Esta acción no se puede deshacer.
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setConfirmDelete(null)}
                style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--color-border-secondary)', background: 'transparent', color: 'var(--color-text-secondary)', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
                Cancelar
              </button>
              <button onClick={handleDeleteConfirmed}
                style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#C53030', color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}